import CryptoJS from "crypto-js";

import { NmsrpPacketManager } from "./messageBuilder";
import generateRandomString from "./utilities/generators";
import MsrpUri from "./msrpUri";

class MsrpAuth {
  static maxNc = 5;
  static ncStr = ["00000001", "00000002", "00000003", "00000004", "00000005"];

  constructor(endpoint, secure = false) {
    this.manager = new NmsrpPacketManager();
    this.endpoint = endpoint;
    this.username = this.endpoint.username;
    this.password = this.endpoint.password;
    this.sessionId = this.endpoint.sessionId;
    // Use authToPath (original auth path) instead of toPath which gets updated
    this.authTarget = this.endpoint.authToPath.toString();
    this.secure = secure;
    this.state = 0;
    this.sentNonce = null;
    this.sentNonceInt = 0;
    this.ncCount = 0;
  }

  //this functions generates an MD5 hash of the input string.
  md5Hash(input) {
    return CryptoJS.MD5(input).toString(CryptoJS.enc.Hex);
  }

  // HA1 = MD5(username:realm:password). Always recompute it here using the realm
  // SCont sends in the 401 challenge and the user's plaintext password. Do NOT
  // feed the stored HA1 ("h1_<md5hex>") in as the password — that would double-hash.
  calculateHA1(realm) {
    const ha1Text = `${this.username}:${realm}:${this.password}`;
    return this.md5Hash(ha1Text);
  }

  calculateHA2(method, uri) {
    //console.log("methis is :" + method + " uri is : "+ uri+ " \n\n");
    const ha2Text = `${method}:${uri}`;
    return this.md5Hash(ha2Text);
  }

  calculateResponse(ha1, nonce, nc, cnonce, qop, ha2) {
    //console.log("ha1 is : "+ha1+"\n"
    //+"nonce is : "+nonce + "\nnc is : "+ nc
    //+"\ncnonce is : "+cnonce + "\nqop is : "+ qop
    //+"\n ha2 is : "+ha2);
    const responseText = `${ha1}:${nonce}:${nc}:${cnonce}:${qop}:${ha2}`;
    return this.md5Hash(responseText);
  }

  buildAuthRequest(toPath, fromPath) {
    const auth_msg = this.manager.createEmptyMessage();

    this.manager.setMethod(auth_msg, "AUTH");
    this.manager.setTransactionId(auth_msg, generateRandomString(16));
    this.manager.setToPath(auth_msg, toPath);
    this.manager.setFromPath(auth_msg, fromPath);
    // this.manager.setMessageId(auth_msg, generateRandomString(8));

    // this.manager.setByteRanges(auth_msg, 1, auth_msg.bodyLength, auth_msg.bodyLength);
    console.log("Sending AUTH request: " + this.manager.messageToString(auth_msg));

    return auth_msg;
  }

  buildAuthorization(realm, nonce, qop) {
    //console.log("endpoint to path is : "+this.endpoint.fromPath.toString());
    console.log("building Authorization...");

    const ha1 = this.calculateHA1(realm);
    const uri = this.authTarget;
    const ha2 = this.calculateHA2("AUTH", uri);

    const cnonce = generateRandomString(16);
    const nc = MsrpAuth.ncStr[this.ncCount % MsrpAuth.maxNc];
    this.ncCount++;

    const response = this.calculateResponse(ha1, nonce, nc, cnonce, qop, ha2);

    const msg = this.manager.createEmptyMessage();
    this.manager.setTransactionId(msg, generateRandomString(16));
    this.manager.setMethod(msg, "AUTH");
    // CRITICAL: Always use authToPath for AUTH messages (not toPath which changes after 200 OK)
    msg.setHeader("To-Path", this.endpoint.authToPath.toString());
    msg.setHeader("From-Path", this.endpoint.fromPath.toString());
    msg.setHeader(
      "Authorization",
      `Digest username="${this.endpoint.username}", realm="${realm}", nonce="${nonce}", qop=${qop}, nc=${nc}, cnonce="${cnonce}", response="${response}"`
    );

    console.log("Authorization message built: ", this.manager.messageToString(msg));

    return msg;
  }

  handle401Unauthorized(rawMessage) {
    console.log("Handling 401 Unauthorized...\n lets get www header from this packet " + rawMessage);

    const lines = rawMessage.split("\r\n");

    let realm = "";
    let qop = "";
    let nonce = "";

    for (const line of lines) {
      if (line.startsWith("WWW-Authenticate: ")) {
        const realmMatch = line.match(/realm="([^"]+)"/);
        const qopMatch = line.match(/qop="([^"]+)"/);
        const nonceMatch = line.match(/nonce="([^"]+)"/);

        if (realmMatch) realm = realmMatch[1];
        if (qopMatch) qop = qopMatch[1];
        if (nonceMatch) nonce = nonceMatch[1];

        break;
      }
    }

    console.log(`Parsed values - realm: ${realm}, qop: ${qop}, nonce: ${nonce}`);
    return this.buildAuthorization(realm, nonce, qop);
  }

  handle200Ok(rawMessage) {
    console.log("Handling 200 OK... Parsing the Use-Path and other headers.");

    const lines = rawMessage.split("\n");

    let usePath = "";
    let sessionId = "";
    let expires = 0;
    let authInfo = {};

    lines.forEach((line) => {
      if (line.startsWith("Use-Path:")) {
        usePath = line.split(": ")[1].trim();
        console.log(`Use-Path header value: ${usePath}`);

        const uriParts = usePath.split("/");
        const sessionAndProtocol = uriParts.pop().split(";");
        const sessionId = sessionAndProtocol[0];
        const protocol = sessionAndProtocol[1];

        const hostPort = uriParts[2].split(":");
        const host = hostPort[0];
        const port = hostPort[1];

        console.log(`Host: ${host}`);
        console.log(`Port: ${port}`);
        console.log(`Session ID: ${sessionId}`);
        console.log(`Protocol: ${protocol}`);

        // Update toPath with Use-Path from server (for sending messages)
        // CRITICAL: authToPath is never updated - it's always the original auth target
        this.endpoint.toPath = new MsrpUri(
          this.endpoint.username,
          host,
          port,
          sessionId,
          protocol,
          this.endpoint.role,
          this.endpoint.companyDomain,
          this.endpoint.subrole
        );
        console.log("New toPath after auth is: " + this.endpoint.toPath.toString());
        console.log("Original authToPath (unchanged): " + this.endpoint.authToPath.toString());
      } else if (line.startsWith("Expires:")) {
        expires = parseInt(line.split(": ")[1].trim(), 10);
        console.log(`Expires header value: ${expires}`);
      } else if (line.startsWith("Authentication-Info:")) {
        const authInfoParts = line.split(": ")[1].split(", ");
        authInfoParts.forEach((part) => {
          const [key, value] = part.split("=");
          authInfo[key.trim()] = value.replace(/"/g, "");
        });
        console.log("Authentication-Info header data:", authInfo);
      }
    });

    this.endpoint.usePath = usePath;

    //this.endpoint.expires = expires;
    //this.endpoint.authInfo = authInfo; we can add this data to endpoint if needed

    const transaction = generateRandomString(16);
    const msg = this.manager.createEmptyMessage();
    this.manager.setTransactionId(msg, transaction);
    this.manager.setMethod(msg, "SEND");
    this.manager.setToPath(msg, this.endpoint.toPath.toString());
    this.manager.setFromPath(msg, this.endpoint.fromPath.toString());
    this.manager.setMessageId(msg, generateRandomString(16));
    this.manager.setByteRanges(msg, 1, 0, 0);
    this.manager.setBody(msg, "");

    this.endpoint.addPendingMessage(transaction, msg);

    return msg;
  }

  // TODO: refactor this part
  handleSendPing() {
    const transaction = generateRandomString(16);
    const msg = this.manager.createEmptyMessage();
    this.manager.setTransactionId(msg, transaction);
    this.manager.setMethod(msg, "SEND");
    this.manager.setToPath(msg, this.endpoint.toPath.toString());
    this.manager.setFromPath(msg, this.endpoint.fromPath.toString());
    this.manager.setMessageId(msg, generateRandomString(16));
    this.manager.setByteRanges(msg, 1, 0, 0);
    this.manager.setBody(msg, "");

    this.endpoint.addPendingMessage(transaction, msg);

    return msg;
  }

  extractValue(lines, key) {
    for (const line of lines) {
      if (line.startsWith(key)) {
        const parts = line.split("=");
        if (parts.length > 1) {
          return parts[1].replace(/"/g, "").trim();
        }
      }
    }
    return "";
  }
}

export default MsrpAuth;
